import type { AdminOrgDto } from '@carcheck/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CompanyFormFields } from './CompanyFormFields';
import { updateOrg } from '../../../api/admin-orgs';
import {
  companyFormSchema,
  toUpdateBody,
  valuesFromOrg,
  type CompanyFormValues,
} from '../_lib/companyForm';
import type { UseOrgWriteResult } from '../_lib/useOrgWrite';

/**
 * «Редактировать» (AUT-403) — `PATCH /admin/orgs/:id` with only the changed
 * keys; an emptied optional field clears it. Mounted only when the card
 * holds UNMASKED contacts (`piiRevealed`): prefilling a masked phone and
 * saving it back would overwrite the real one. Nothing changed → closes.
 */
export const EditCompanyDialog = ({
  org,
  open,
  write,
  onClose,
}: {
  org: AdminOrgDto;
  open: boolean;
  write: UseOrgWriteResult;
  onClose: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const form = useForm<CompanyFormValues>({
    resolver: zodResolver(companyFormSchema),
    mode: 'onTouched',
    defaultValues: valuesFromOrg(org),
  });

  useEffect(() => {
    if (open) form.reset(valuesFromOrg(org));
    // Re-seed on open only; a background refetch must not wipe typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = (values: CompanyFormValues): void => {
    const body = toUpdateBody(values, org);
    if (Object.keys(body).length === 0) return onClose();
    void write
      .run(() => updateOrg(org.id, body), 'companies.edit.success')
      .then((ok) => {
        if (ok) onClose();
      });
  };

  return (
    <Dialog open={open} onClose={write.busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('companies.edit.title')}</DialogTitle>
      <form noValidate onSubmit={form.handleSubmit(submit)}>
        <DialogContent>
          <CompanyFormFields
            register={form.register}
            control={form.control}
            setValue={form.setValue}
            errors={form.formState.errors}
            disabled={write.busy}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={write.busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={write.busy} loading={write.busy}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
